import React from "react";

import { isNullOrUndefined, useTranslation } from "utils";

import { CropResidueType } from "consts";

import { CROP_TYPE_FCPO, PracticeCrop, WithInjectedName } from "models";

import { AlertBanner, Flex, Form, KlimRadioGroup, TextArea, Typography } from "components";

import { CROP } from "pages/app/all-field-view/constants/cropForm.ts";

export type BulkResidueSectionProps = {
  cropType: CROP_TYPE_FCPO;
  residueType: CropResidueType | null;
  residueCrops: WithInjectedName<PracticeCrop>[];
  // shared answer across the selection, or null when the crops disagree
  isResidueProduced: boolean | null;
  hasMixedResidue: boolean;
  residueNote: string | null;
  hasMixedResidueNotes: boolean;
  disabled: boolean;
};

// residue management for the whole selection: whether residue was left on the field, plus the note explaining an
// unusual answer. Mirrors the residue block of Timeline's crop form, including the "multiple values" hint for a mixed selection.
export const BulkResidueSection = ({
  cropType,
  residueType,
  residueCrops,
  isResidueProduced,
  hasMixedResidue,
  residueNote,
  hasMixedResidueNotes,
  disabled,
}: BulkResidueSectionProps) => {
  const { t } = useTranslation(["allFieldView", "modules"]);
  const form = Form.useFormInstance();
  const residueAnswer: boolean | null | undefined = Form.useWatch([CROP, "hasResidue"], form);
  const isSideCrop = cropType === CROP_TYPE_FCPO.COVER || cropType === CROP_TYPE_FCPO.UNDERSOWING;
  const cropsAnsweredUnusually = isNullOrUndefined(residueAnswer)
    ? []
    : residueCrops.filter((crop) => crop.residueLeftByDefault !== null && crop.residueLeftByDefault !== residueAnswer);

  return (
    <>
      <Flex justify="space-between" align="center" gap="1rem">
        <Typography variant="paragraph-m">
          {isSideCrop
            ? t("allFieldView:cropForm.residue.residueLeftSideCrop")
            : t(`allFieldView:cropForm.residue.${residueType ? `${residueType}.` : ""}residueLeft`)}
        </Typography>
        <Form.Item
          name={[CROP, "hasResidue"]}
          rules={[{ required: true, message: t("modules:bauEntryDrawer.crops.residueLeft.error") }]}
          initialValue={isResidueProduced}
          help={hasMixedResidue && isResidueProduced === null ? t("allFieldView:cropForm.mixedValues") : undefined}
        >
          <KlimRadioGroup
            className="radio-bg-white"
            size="large"
            type="tertiary"
            dataTestId="bulk-residue-toggle"
            disabled={disabled}
            options={[
              { value: true, label: t("allFieldView:cropForm.yes") },
              { value: false, label: t("allFieldView:cropForm.no") },
            ]}
            optionType="button"
            buttonStyle="solid"
            fullWidth
          />
        </Form.Item>
      </Flex>
      {cropsAnsweredUnusually.length > 0 && (
        <Flex vertical gap="1rem">
          <AlertBanner
            type="warning"
            iconName="tabler/alert-triangle"
            iconAlign="start"
            dataTestId="bulk-residue-warning"
            title={t("allFieldView:cropForm.residue.warningTitle")}
            description={t(
              residueType ? `allFieldView:cropForm.residue.${residueType}.warningDescription` : "allFieldView:cropForm.residue.warningDescription",
              {
                cropName: cropsAnsweredUnusually.map((crop) => crop.name).join(", "),
                value: residueAnswer ? t("allFieldView:cropForm.yes") : t("allFieldView:cropForm.no"),
              }
            )}
            headingSize="h5"
          />
          <TextArea
            dataTestId="bulk-residue-note"
            name={[CROP, "sanityNoteResidue"]}
            initialValue={residueNote}
            label={t("allFieldView:cropForm.residue.note")}
            rules={[{ required: true, message: t("allFieldView:cropForm.errorMessages.residueNoteRequired") }]}
            help={hasMixedResidueNotes ? t("allFieldView:cropForm.mixedValues") : undefined}
            disabled={disabled}
            autoSize
          />
        </Flex>
      )}
    </>
  );
};
